import { Button, Icon } from '@hibro/ui-client';
import type { ReactNode } from 'react';

import { useTranslator } from '../i18n/i18n';

export interface DetachedPlaceholderProps {
  /** Display title of the view whose content now lives in another window. */
  title: string;
  onFocusDetached?: () => void;
  onAttachView?: () => void;
}

/**
 * What a `migrate`-detached surface leaves behind in the workbench (§5.8).
 *
 * Shared by the editor area's two hosts — the single-active {@link EditorSlot}
 * and the dockview instance tabs — because both can hold a migrated instance
 * and this is the only Focus/Attach control the user gets. Renders centred in
 * whatever box the caller gives it.
 */
export function DetachedPlaceholder({
  title,
  onFocusDetached,
  onAttachView,
}: DetachedPlaceholderProps): ReactNode {
  const { t } = useTranslator();
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon spec={{ type: 'builtin', id: 'layout' }} size={22} />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">
          {t('workbench.editor.detachedNotice', { title })}
        </p>
        <p className="text-[13px] text-muted-foreground">
          {t('workbench.editor.detachedHint')}
        </p>
      </div>
      {onFocusDetached || onAttachView ? (
        <div className="flex gap-2">
          {onFocusDetached ? (
            <Button size="sm" onClick={onFocusDetached}>
              {t('workbench.editor.focusWindow')}
            </Button>
          ) : null}
          {onAttachView ? (
            <Button variant="outline" size="sm" onClick={onAttachView}>
              {t('workbench.editor.attachBack')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
